import { formatMoney } from '@app/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { orgHref } from '../lib/public-router'
import { readCivilDate } from '../lib/today'
import { useTRPC } from '../lib/trpc'
import { Button, Card, Figure, Notice } from '../ui'
import { PublicNotFound } from '../ui/ErrorPage'
import { PublicShell } from '../ui/PublicShell'
import './gift.css'

/**
 * The page the letter confirming a membership request opens. It goes to a
 * donor already on file whose own address the request came from, and
 * following it is the proof the request is theirs: confirming makes them the
 * head of the family it asks for, as the office's approval would (product
 * owner, 2026-10-26). No login: the `?token=` is the whole authority, read on
 * the page of the org the request was made at.
 *
 * Confirming twice is the same as once. A link past its window, or one the
 * office has declined, says so; a broken one says what the server said about
 * it.
 */
export function ConfirmMembershipPage({
  orgId,
  onLogin,
}: {
  orgId: string
  onLogin?: () => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const token = new URLSearchParams(window.location.search).get('token') ?? ''
  const link = { shortId: orgId, token }

  const org = useQuery(trpc.donation.org.queryOptions({ shortId: orgId }))
  const request = useQuery({
    ...trpc.donation.membershipConfirmView.queryOptions(link),
    enabled: token.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  })
  /* Read again whatever the answer, so a link the office decided meanwhile,
     or one that ran out, shows where it stands rather than a stale button. */
  const confirm = useMutation({
    ...trpc.donation.confirmMembership.mutationOptions(),
    onSettled: () =>
      qc.invalidateQueries({
        queryKey: trpc.donation.membershipConfirmView.queryKey(link),
      }),
  })

  /* The button goes once it has done its work, so the sentence saying what
     happened takes the keyboard in its place. */
  const done = useRef<HTMLDivElement>(null)
  const confirmed = request.data?.state === 'confirmed'
  useEffect(() => {
    if (confirm.isSuccess && confirmed) done.current?.focus()
  }, [confirm.isSuccess, confirmed])

  if (org.isLoading || org.error || !org.data) {
    return (
      <PublicShell name={null} orgId={null} onLogin={onLogin}>
        {org.isLoading ? (
          <div className="gift">
            <Card className="gift__card">
              <Notice>Loading…</Notice>
            </Card>
          </div>
        ) : (
          <PublicNotFound />
        )}
      </PublicShell>
    )
  }

  const o = org.data
  const view = request.data ?? null
  const each = view?.cadence === 'annual' ? 'a year' : 'a month'

  return (
    <PublicShell
      name={o.name}
      nameHe={o.nameHe}
      language={o.publicLanguage}
      orgId={o.shortId}
      acceptsDonations={o.acceptsDonations}
      contact={o}
      onLogin={onLogin}
    >
      <div className="gift">
        <Card className="gift__card">
          <h1 className="gift__title">Confirm your membership</h1>
          {token && request.isLoading ? (
            <p className="gift__muted">Loading…</p>
          ) : !token || request.isError || !view ? (
            <Notice>
              {request.error?.message ??
                'This link is not valid. Please use the link in the letter we sent, or contact the office.'}
            </Notice>
          ) : (
            <>
              <Figure
                amount={`${formatMoney(view.rateCents)} ${each}`}
                label={`${view.tierLabel ?? 'Membership'}, from ${readCivilDate(view.startsOn, 'long')}.`}
              />
              {view.people.length > 0 ? (
                <p className="gift__muted">For {view.people.join(', ')}.</p>
              ) : null}
              {view.state === 'confirmed' ? (
                <div ref={done} tabIndex={-1}>
                  <Notice tone="info">
                    Your membership is set up. A confirmation is on its way by
                    email.
                  </Notice>
                </div>
              ) : view.state === 'declined' ? (
                <Notice>
                  The office could not set up this membership. Please contact
                  them if you have any questions.
                </Notice>
              ) : view.state === 'closed' ? (
                <Notice>
                  This link no longer works. The office will look at your
                  request, or you can contact them.
                </Notice>
              ) : (
                <>
                  <p className="gift__muted">
                    We already have you on file, so we ask you to confirm the
                    request is yours. Confirming sets up the membership on your
                    record with us, with everybody the form named.
                  </p>
                  {confirm.error && (
                    <Notice tone="error">{confirm.error.message}</Notice>
                  )}
                  <Button
                    disabled={confirm.isPending}
                    onClick={() => confirm.mutate(link)}
                  >
                    Confirm this membership
                  </Button>
                </>
              )}
              <a href={orgHref(o.shortId, '')}>Back to {o.name}</a>
            </>
          )}
        </Card>
      </div>
    </PublicShell>
  )
}
